import * as React from "react";

import AppModals from "@app/AppModals";
import { Drawer } from "@replohq/lattice/components/drawer/Drawer";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@replohq/lattice/components/sidebar/Sidebar";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { cn } from "@replohq/lattice/utils/cn";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { AdminPanel } from "@v2/features/admin/AdminPanel";
import {
  useAdminPanelActions,
  useAdminPanelStore,
} from "@v2/features/admin/adminPanelStore";
import { AgentLaunchProvider } from "@v2/features/agent-beta/AgentLaunchProvider";
import ManageChats from "@v2/features/chat/components/chat-elements/ManageChats";
import MobileChatHeader from "@v2/features/chat/components/MobileChatHeader";
import { useChatSidebar } from "@v2/features/chat/hooks/useChatSidebar";
import DashboardSidebar from "@v2/features/dashboard/sidebar/DashboardSidebar";
import { isWelcomeFromV1Open } from "@v2/features/home/welcomeFromV1";
import {
  InAppAnnouncementProvider,
  InAppAnnouncements,
} from "@v2/features/in-app-announcement/InAppAnnouncementProvider";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { routes } from "@v2/utils/router";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

function ChatRouteSidebarListener() {
  const { pathname } = useLocation();
  const { open, openMobile, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isExpanded = isMobile ? openMobile : open;
  const shopsChatRoute = `${routes.shops.chat}/*`;
  const agentRoute = `${routes.agent.root}/*`;

  useOnValueChange(pathname, (newPath, oldPath) => {
    const wasChat = Boolean(
      oldPath &&
      (matchPath(shopsChatRoute, oldPath) || matchPath(agentRoute, oldPath)),
    );
    const isChat = Boolean(
      newPath &&
      (matchPath(shopsChatRoute, newPath) || matchPath(agentRoute, newPath)),
    );
    if (!wasChat && isChat && isExpanded) {
      toggleSidebar();
    }
  });

  return null;
}

const DashboardLayout: React.FC<
  React.PropsWithChildren<{ contentIsCard: boolean }>
> = ({ children, contentIsCard }) => {
  const isDebugModeAllowed = useIsDebugMode();
  const isDebugPanelVisible = useAdminPanelStore((state) => state.isVisible);
  const { toggle: toggleDebugMode } = useAdminPanelActions();
  useReploHotkeys(isDebugModeAllowed ? { debug: toggleDebugMode } : {});
  const { pathname, search } = useLocation();
  const workspace = useCurrentWorkspace();

  const mobileLayoutRoutes = [
    routes.agent.root,
    routes.shops.home,
    routes.shops.products.list,
    routes.shops.products.edit,
    routes.shops.products.new,
    routes.shops.chat,
  ];

  const isMobileLayoutRoute = mobileLayoutRoutes.some((route) => {
    return Boolean(matchPath(route, pathname));
  });

  const isShopsHome = Boolean(matchPath(routes.shops.home, pathname));
  const isAgentHome = Boolean(matchPath(routes.agent.root, pathname));
  const isHomePage = isShopsHome || isAgentHome;
  const hasWelcomeModal =
    isShopsHome &&
    isWelcomeFromV1Open({
      paymentProcessor: workspace?.paymentProcessor,
      searchParams: new URLSearchParams(search),
    });
  const { open: isChatSidebarOpen, setOpen: setIsChatSidebarOpen } =
    useChatSidebar();
  const isMobile = useIsMobile();

  return (
    <div className={cn("flex flex-col", isMobile ? "h-[100dvh]" : "h-screen")}>
      <AppModals />
      {isDebugModeAllowed && isDebugPanelVisible && <AdminPanel />}
      <InAppAnnouncementProvider surface="theme-builder">
        <AgentLaunchProvider>
          <div className="h-screen grow no-scrollbar overflow-y-scroll">
            <SidebarProvider className="h-full w-full">
              <ChatRouteSidebarListener />
              <DashboardSidebar />
              {isMobile && (
                <>
                  <MobileChatHeader />
                  <Drawer
                    open={isChatSidebarOpen}
                    onOpenChange={setIsChatSidebarOpen}
                    title="All Chats"
                  >
                    <ManageChats />
                  </Drawer>
                </>
              )}

              <SidebarInset
                UNSAFE_className={cn(
                  isMobileLayoutRoute && isMobile
                    ? "px-0 py-0 pt-14"
                    : "py-3 pr-3",
                )}
              >
                {contentIsCard ? (
                  <div
                    className={cn(
                      "max-sm:border-0 max-sm:rounded-none max-sm:shadow-none shadow-2xs border-0.5 border-border no-scrollbar rounded-[10px] bg-white flex flex-col items-center h-full",
                      !isHomePage && "overflow-y-scroll",
                    )}
                  >
                    <div
                      className={cn(
                        "no-scrollbar flex flex-col h-full w-full",
                        isHomePage
                          ? "items-stretch"
                          : "overflow-y-scroll p-6 pb-0 max-w-[1440px] items-center max-sm:p-0",
                      )}
                    >
                      <Outlet />
                      {children}
                    </div>
                  </div>
                ) : (
                  <>
                    <Outlet />
                    {children}
                  </>
                )}
              </SidebarInset>
            </SidebarProvider>
          </div>
        </AgentLaunchProvider>
        {!hasWelcomeModal && <InAppAnnouncements />}
      </InAppAnnouncementProvider>
    </div>
  );
};

export default DashboardLayout;
